/**
 * Hook for wallet ownership verification in YaaS Client Console.
 *
 * Two-path verification: signMessage (primary) with micro-transfer fallback.
 * signMessage proofs use Ed25519 signatures verified cryptographically server-side.
 * Micro-transfers are verified on-chain via Helius.
 *
 * Adapted from packages/apps/main/src/hooks/yaas/useWalletVerification.ts.
 * Key difference: accepts a message string directly (not a numeric positionId),
 * so callers can build action-specific messages (create, edit-destination, etc.).
 */

import { VERIFICATION_WALLET_ADDRESS } from "@solomon-apps/config";
import { confirmTransaction, useTransactionSigner, useWallet, useWalletMessageSigner } from "@solomon-apps/shared";
import { PublicKey } from "@solomon-labs/publickey";
import { ConnectionManager } from "@solomon-labs/solana-connection";
import type { SolanaAddress, SolanaTransactionSignature } from "@solomon-labs/types";
import { useCallback, useState } from "react";

import { buildClientVerificationTransfer } from "./verificationTransfer";

/**
 * Mirrors yaas-api's MICRO_TRANSFER_MAX_AGE_SECONDS: the backend rejects
 * micro-transfer proofs whose transaction is older than 15 minutes. Used
 * by multi-step flows (proof captured at one step, submitted at a later
 * one) to fail fast with actionable copy instead of a backend rejection
 * after the user already paid for the transfer.
 */
export const MICRO_TRANSFER_PROOF_MAX_AGE_MS = 15 * 60 * 1000;

export interface VerificationProof {
    method: "signMessage" | "microTransfer";
    message?: string;
    signature: string;
    signedMessage?: string;
    verificationNonce?: string;
    /** Epoch ms when the proof was produced. For micro-transfers this
     *  approximates the on-chain blockTime the backend ages against. */
    obtainedAt?: number;
}

/**
 * Why a verification attempt failed, classified so the UI can react
 * correctly. `cancelled` = the user actively dismissed the wallet prompt
 * (retrying the same method is sensible). `couldNotSign` = the wallet
 * couldn't produce a message signature at all (e.g. hardware wallets like
 * Ledger) — the sound next step is the micro-transfer path, not a retry.
 */
export type VerificationFailureReason = "cancelled" | "couldNotSign";

/**
 * Outcome of one `verify()` attempt. Failures carry everything the caller
 * needs in the return value itself — reading the hook's `error` state right
 * after `await verify(...)` reads the *previous* render's value (stale
 * closure), which is exactly the bug this shape exists to prevent.
 */
export type VerifyResult =
    | { proof: VerificationProof }
    | {
          proof: null;
          /** User-facing description of why the attempt failed. */
          errorMessage: string;
          /** Classification for CTA hierarchy; null when the failure fits
           *  neither bucket (e.g. a broadcast transfer that never confirmed). */
          reason: VerificationFailureReason | null;
          /** Set when a micro-transfer was broadcast before the failure —
           *  the user may have paid; surface this reference for support. */
          transactionSignature: string | null;
      };

export interface VerifyOptions {
    /** Constrain which verification path to attempt. When omitted, tries
     *  signMessage first and auto-falls-back to micro-transfer. */
    method?: "signMessage" | "microTransfer";
    /** Server-issued position verification nonce memo, preserved through fallback/retry. */
    microTransferMemo?: string;
}

export interface UseWalletVerificationResult {
    verify: (
        walletAddress: string,
        messageToSign: string,
        options?: VerifyOptions,
        onFallback?: () => void,
        onConfirming?: (transactionSignature: string) => void
    ) => Promise<VerifyResult>;
    isVerifying: boolean;
    verificationMethod: "signMessage" | "microTransfer" | null;
    error: string | null;
}

export function useWalletVerification(): UseWalletVerificationResult {
    const { connected, publicKey } = useWallet();
    const { signer, ready: signerReady } = useTransactionSigner();
    const signWalletMessage = useWalletMessageSigner();
    const [isVerifying, setIsVerifying] = useState(false);
    const [verificationMethod, setVerificationMethod] = useState<"signMessage" | "microTransfer" | null>(null);
    const [error, setError] = useState<string | null>(null);

    const verify = useCallback(
        async (
            walletAddress: string,
            messageToSign: string,
            options?: VerifyOptions,
            onFallback?: () => void,
            onConfirming?: (transactionSignature: string) => void
        ): Promise<VerifyResult> => {
            setIsVerifying(true);
            setError(null);
            setVerificationMethod(null);

            const constrainedMethod = options?.method;

            const fail = (
                errorMessage: string,
                reason: VerificationFailureReason | null = null,
                transactionSignature: string | null = null
            ): VerifyResult => {
                setError(errorMessage);
                setIsVerifying(false);
                return { proof: null, errorMessage, reason, transactionSignature };
            };

            if (!connected || !publicKey) {
                return fail("Wallet not connected");
            }

            if (publicKey.toBase58() !== walletAddress) {
                return fail("Wallet address mismatch");
            }

            if (!signerReady || !signer) {
                return fail("Transaction signer not ready");
            }

            // Primary path: signMessage (skip if constrained to microTransfer)
            if (constrainedMethod !== "microTransfer") {
                try {
                    setVerificationMethod("signMessage");
                    const messageProof = await signWalletMessage(walletAddress, messageToSign);

                    setIsVerifying(false);
                    return {
                        proof: {
                            method: "signMessage",
                            message: messageToSign,
                            ...messageProof,
                            obtainedAt: Date.now()
                        }
                    };
                } catch (err: unknown) {
                    const isUserRejection =
                        (typeof err === "object" &&
                            err !== null &&
                            "code" in err &&
                            (err.code === 4001 || err.code === "4001")) ||
                        (err instanceof Error && /reject|denied|cancel|disapproved/i.test(err.message));
                    // When constrained to signMessage, any failure is terminal.
                    // When unconstrained, only user rejections are terminal.
                    if (isUserRejection || constrainedMethod === "signMessage") {
                        const detail =
                            typeof err === "object" &&
                            err !== null &&
                            "message" in err &&
                            typeof err.message === "string"
                                ? err.message.trim()
                                : "";
                        return fail(
                            isUserRejection
                                ? "Signature request was rejected"
                                : detail
                                  ? `Message signing failed: ${detail}`
                                  : "Message signing failed. Reconnect your wallet and try again.",
                            isUserRejection ? "cancelled" : "couldNotSign"
                        );
                    }
                    // Unconstrained + non-rejection → fall through to micro-transfer
                }
            }

            // Fallback / explicit path: micro-transfer
            let broadcastSignature: string | null = null;
            try {
                setVerificationMethod("microTransfer");
                onFallback?.();

                const blockhashResponse = await ConnectionManager.getRpcClient().getLatestBlockhash();
                if (!blockhashResponse) {
                    throw new Error("Failed to get recent blockhash");
                }
                const { blockhash: recentBlockhash, lastValidBlockHeight } = blockhashResponse;

                const from = publicKey;
                const to = new PublicKey(VERIFICATION_WALLET_ADDRESS as SolanaAddress);

                const transaction = buildClientVerificationTransfer({
                    payer: from,
                    recipient: to,
                    blockhash: recentBlockhash,
                    memo: options?.microTransferMemo
                });

                const serializedTx = transaction.serialize(false);
                const transactionSignature = await signer.signAndSendTransaction(serializedTx);
                broadcastSignature = transactionSignature;
                onConfirming?.(transactionSignature);

                const result = await confirmTransaction(transactionSignature as SolanaTransactionSignature, {
                    lastValidBlockHeight
                });
                if (!result.confirmed) {
                    throw new Error(result.error || "Verification transaction failed to confirm");
                }

                setIsVerifying(false);
                return {
                    proof: {
                        method: "microTransfer",
                        signature: transactionSignature,
                        obtainedAt: Date.now()
                    }
                };
            } catch (err: unknown) {
                const message = err instanceof Error ? err.message : "Wallet verification failed";
                // A rejection can only come from the wallet prompt — once the
                // transaction is broadcast, "cancel"-looking RPC errors are
                // confirmation failures, not user cancels.
                const isUserRejection =
                    broadcastSignature === null &&
                    err instanceof Error &&
                    /reject|denied|cancel|disapproved/i.test(err.message);
                return fail(message, isUserRejection ? "cancelled" : null, broadcastSignature);
            }
        },
        [connected, publicKey, signer, signerReady, signWalletMessage]
    );

    return { verify, isVerifying, verificationMethod, error };
}
